import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '@/lib/api';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  role: string;
  createdAt: string;
}

interface TenantContextType {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  isLoading: boolean;
  switchTenant: (tenantId: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const TENANT_STORAGE_KEY = 'realvisor_current_tenant';

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (session?.access_token) {
      api.setToken(session.access_token);
      loadTenants();
    } else {
      setTenants([]);
      setCurrentTenant(null);
      setIsLoading(false);
    }
  }, [session]);

  const loadTenants = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<Tenant[]>('/tenants/me');
      setTenants(data);

      // Restore last selected tenant or use first one
      const storedTenantId = localStorage.getItem(TENANT_STORAGE_KEY);
      const storedTenant = data.find((t) => t.id === storedTenantId);
      
      if (storedTenant) {
        setCurrentTenant(storedTenant);
        api.setTenantId(storedTenant.id);
      } else if (data.length > 0 && data[0]) {
        setCurrentTenant(data[0]);
        api.setTenantId(data[0].id);
        localStorage.setItem(TENANT_STORAGE_KEY, data[0].id);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchTenant = (tenantId: string) => {
    const tenant = tenants.find((t) => t.id === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
      api.setTenantId(tenant.id);
      localStorage.setItem(TENANT_STORAGE_KEY, tenant.id);
    }
  };

  return (
    <TenantContext.Provider value={{ tenants, currentTenant, isLoading, switchTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
